import { AutoReloadOnNewVersion, GTMSendPageView, lazyWithRetry, RequireAuth } from "@getsoren/shared-module";
import * as Sentry from "@sentry/react";
import { Suspense } from "react";
import { createBrowserRouter, createRoutesFromElements, Outlet, Route, RouterProvider } from "react-router";
import { SAAS_AXIOS_INSTANCE } from "@/api/customSaasInstance";
import PageLeftTracker from "@/components/Utils/PageLeftTracker";
import ErrorFallback from "@/components/Utils/Router/components/ErrorFallback";
import HomeRedirect from "@/components/Utils/Router/components/HomeRedirect";
import PageLoading from "@/components/Utils/Router/components/PageLoading";
import PermissionRoute from "@/components/Utils/Router/components/PermissionRoute";
import ROUTES from "@/constants/routes";
import useAuth from "@/hooks/useUser";
import { version } from "~/package.json";

const ProtectedProviders = lazyWithRetry(() => import("@/context/ProtectedProviders"));
const BackofficeContainer = lazyWithRetry(() => import("@/features/BackofficeContainer/BackofficeContainer"));
const NotFound = lazyWithRetry(() => import("@/pages/NotFound"));
const Billing = lazyWithRetry(() => import("@/pages/Deals/Deal/Billing/Billing"));
const Deal = lazyWithRetry(() => import("@/pages/Deals/Deal/Deal"));
const Deals = lazyWithRetry(() => import("@/pages/Deals/Deals"));
const Planning = lazyWithRetry(() => import("@/pages/Planning"));
const Login = lazyWithRetry(() => import("@/pages/Login"));
const ForgotPassword = lazyWithRetry(() => import("@/pages/ForgotPassword"));
const Profile = lazyWithRetry(() => import("@/pages/Profile"));
const Requests = lazyWithRetry(() => import("@/pages/Requests/Requests"));
const Request = lazyWithRetry(() => import("@/pages/Requests/Request/Request"));
const Info = lazyWithRetry(() => import("@/pages/Info"));
const Resources = lazyWithRetry(() => import("@/pages/Resources/Resources"));
const Resource = lazyWithRetry(() => import("@/pages/Resources/Resource/Resource"));
const Analytics = lazyWithRetry(() => import("@/pages/Analytics"));
const QueryNotFound = lazyWithRetry(() => import("@/pages/QueryNotFound"));
const Agencies = lazyWithRetry(() => import("@/pages/Agencies/Agencies"));
const Agency = lazyWithRetry(() => import("@/pages/Agencies/Agency/Agency"));
const Messages = lazyWithRetry(() => import("@/pages/Messages"));
const Users = lazyWithRetry(() => import("@/pages/Users/Users"));

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

const router = sentryCreateBrowserRouter(
  createRoutesFromElements(
    <Route
      element={
        <Sentry.ErrorBoundary fallback={<ErrorFallback fullPage />}>
          <AutoReloadOnNewVersion version={version}>
            <GTMSendPageView data={{ platform: "web_supplier" }} />
            <PageLeftTracker />
          </AutoReloadOnNewVersion>
        </Sentry.ErrorBoundary>
      }
    >
      <Route
        element={
          <Suspense fallback={<PageLoading />}>
            <ProtectedProviders>
              <BackofficeContainer />
            </ProtectedProviders>
          </Suspense>
        }
      >
        {/* Protected route */}
        <Route
          element={
            <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
              <RequireAuth isLogged={useAuth} Fallback={<PageLoading />} axios={SAAS_AXIOS_INSTANCE} />
            </Sentry.ErrorBoundary>
          }
        >
          {/* Home redirection */}
          <Route path="/" element={<HomeRedirect />} />

          {/* Error request route */}
          <Route path={ROUTES.queryError} element={<QueryNotFound />} />

          {/* Deals */}
          <Route element={<PermissionRoute feature="deals" />}>
            <Route path={`${ROUTES.deals}/*`} element={<Deals />}>
              <Route path=":tabs" element={<Deals />} />
            </Route>

            <Route path={ROUTES.deal}>
              <Route path=":id" element={<Deal />} />
              <Route path=":id/billing" element={<Billing />} />
            </Route>

            <Route path="/deals/id/:id" element={<Deal />} />
          </Route>

          {/* Planning — shares the deals permission and the deals bookings endpoint */}
          <Route element={<PermissionRoute feature="deals" />}>
            <Route path={ROUTES.planning} element={<Planning />} />
          </Route>

          {/* Agencies */}
          <Route element={<PermissionRoute feature="agencies" />}>
            <Route path={ROUTES.agencies}>
              <Route index element={<Agencies />} />
              <Route path=":id" element={<Agency />} />
            </Route>
          </Route>

          {/* Requests */}
          <Route element={<PermissionRoute feature="requests" />}>
            <Route path={ROUTES.requests}>
              <Route index element={<Requests />} />
              <Route path=":tab" element={<Requests />} />
              <Route path="request/:id" element={<Request />} />
              <Route path="id/:id" element={<Request />} />
            </Route>
          </Route>

          {/* Resources */}
          <Route element={<PermissionRoute feature="resources" />}>
            <Route path={ROUTES.resources}>
              <Route index element={<Resources />} />
              <Route path=":id" element={<Resource />} />
            </Route>
          </Route>

          {/* Analytics */}
          <Route element={<PermissionRoute feature="analytics" />}>
            <Route path={ROUTES.analytics} element={<Analytics />} />
          </Route>

          {/* Messages */}
          <Route element={<PermissionRoute feature="messages" />}>
            <Route path={ROUTES.messages} element={<Messages />} />
            <Route path={`${ROUTES.messages}/:threadId`} element={<Messages />} />
          </Route>

          {/* Users */}
          <Route element={<PermissionRoute feature="users" />}>
            <Route path={ROUTES.users} element={<Users />} />
          </Route>

          <Route path={ROUTES.profile} element={<Profile />} />
          <Route path={ROUTES.info} element={<Info />} />
        </Route>
      </Route>

      {/* Public route */}
      <Route
        element={
          <Suspense fallback={<PageLoading />}>
            <Outlet />
          </Suspense>
        }
      >
        <Route path={ROUTES.login}>
          <Route path="*" element={<Login />}>
            <Route index path="*" element={<Login />} />
            <Route path=":logout" element={<Login />} />
          </Route>
        </Route>
        <Route path={ROUTES.forgotPassword} element={<ForgotPassword />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Route>,
  ),
);

const Router = () => <RouterProvider router={router} />;

export default Router;
